// src/utils/normalizeProject.js
// Canonical project normalization for DB-ready schema.

function safeObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function isPlainObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function now() {
  return Date.now();
}

function isoNow() {
  return new Date().toISOString();
}

function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function toBool(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (v == null) return false;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "yes" || s === "1";
  }
  return false;
}

function normalizeDocText(s) {
  return String(s == null ? "" : s).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function safeText(v) {
  return v == null ? "" : String(v);
}

function normalizeStringArray(v) {
  return (Array.isArray(v) ? v : [])
    .map((item) => safeText(item).trim())
    .filter(Boolean);
}

function normalizeProjectType(v) {
  const s = (typeof v === "string" ? v.trim().toLowerCase() : "") || "new";
  if (s === "new") return "new";
  if (s === "existing") return "existing";
  if (s.includes("improv")) return "existing";
  return "new";
}

const ACTIVITY_ACTIONS = new Set([
  "created",
  "edited",
  "approved",
  "regenerated",
  "committed",
  "deleted",
]);

const ACTIVITY_ENTITY_TYPES = new Set(["insight", "decision", "overview"]);

const ACTIVITY_ACTORS = new Set(["user", "ai", "system"]);

const DEFAULT_TEAM = Object.freeze({
  owner: "local-user",
  editors: ["local-user"],
  viewers: [],
});
// Future permissions enforcement should read from this metadata once auth exists.

const FEEDBACK_TYPES = new Set([
  "bug",
  "idea",
  "ai_quality",
  "ux_confusion",
  "data_issue",
  "other",
]);

const FEEDBACK_STATUSES = new Set(["new", "acknowledged", "done"]);

const FEEDBACK_SEVERITIES = new Set(["low", "medium", "high"]);

const RESEARCH_METHODS = new Set([
  "user_feedback",
  "interview_notes",
  "usability_testing",
  "survey_results",
  "analytics_data",
  "research_materials",
  "other",
]);

const RESEARCH_SOURCE_TYPES = new Set(["document", "screenshot", "structured", "paste"]);

const DEFAULT_METHOD_BY_TYPE = {
  interviews: "interview_notes",
  messages: "user_feedback",
  tickets: "user_feedback",
  other: "other",
};

const RESEARCH_ANALYSIS_STATUSES = new Set(["ready", "analyzing", "analyzed", "failed"]);
const RESEARCH_ANALYSIS_ERROR_CODES = new Set(["text_too_long", "temporary_unavailable", "processing_failed"]);

function normalizeResearchMethod(method, type) {
  const raw = typeof method === "string" ? method.trim() : "";
  if (RESEARCH_METHODS.has(raw)) return raw;
  const typeKey = typeof type === "string" ? type.trim().toLowerCase() : "";
  return DEFAULT_METHOD_BY_TYPE[typeKey] || "other";
}

function normalizeResearchSourceType(sourceType) {
  const raw = typeof sourceType === "string" ? sourceType.trim() : "";
  if (RESEARCH_SOURCE_TYPES.has(raw)) return raw;
  return "paste";
}

function normalizeResearchItem(item, idx = 0) {
  const it = safeObj(item);
  const t = now();
  const rawText = typeof it.rawText === "string" ? it.rawText : typeof it.text === "string" ? it.text : "";
  const normalizedText = normalizeDocText(rawText);

  const rawType = typeof it.type === "string" ? it.type.trim().toLowerCase() : "";
  const type = rawType || "other";

  const method = normalizeResearchMethod(it.method, type);
  const sourceType = normalizeResearchSourceType(it.sourceType);
  const analysisStatusRaw =
    typeof it.analysisStatus === "string" ? it.analysisStatus.trim().toLowerCase() : "";
  const analysisStatus = RESEARCH_ANALYSIS_STATUSES.has(analysisStatusRaw)
    ? analysisStatusRaw
    : "";
  const analysisErrorCodeRaw =
    typeof it.analysisErrorCode === "string" ? it.analysisErrorCode.trim().toLowerCase() : "";
  const analysisErrorCode = RESEARCH_ANALYSIS_ERROR_CODES.has(analysisErrorCodeRaw)
    ? analysisErrorCodeRaw
    : "";
  const analysisErrorReason =
    typeof it.analysisErrorReason === "string" ? it.analysisErrorReason.trim() : "";
  const analyzedAt = Number.isFinite(Number(it.analyzedAt)) ? Number(it.analyzedAt) : null;
  const analysisUpdatedAt = Number.isFinite(Number(it.analysisUpdatedAt))
    ? Number(it.analysisUpdatedAt)
    : t;

  return {
    ...it,
    id: typeof it.id === "string" && it.id.trim() ? it.id.trim() : `ri_${idx + 1}`,
    type,
    method,
    sourceType,
    rawText,
    normalizedText,
    analysisStatus,
    analysisErrorCode: analysisStatus === "failed" ? analysisErrorCode : "",
    analysisErrorReason: analysisStatus === "failed" ? analysisErrorReason : "",
    analysisRetryable: analysisStatus === "failed" ? it.analysisRetryable === true : false,
    analyzedAt,
    analysisUpdatedAt,
    createdAt: Number.isFinite(Number(it.createdAt)) ? Number(it.createdAt) : t,
    updatedAt: Number.isFinite(Number(it.updatedAt)) ? Number(it.updatedAt) : t,
  };
}

function normalizeVersion(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeInitiativeVersions(init) {
  const next = { ...safeObj(init) };
  const researchItems = Array.isArray(next.researchItems) ? next.researchItems : [];
  const researchFallback = researchItems.length > 0 ? 1 : 0;
  const researchVersion = normalizeVersion(next.researchVersion, researchFallback);
  const insightsVersion = normalizeVersion(next.insightsVersion, 0);
  const tagsVersion = normalizeVersion(next.tagsVersion, 0);

  const insightsMeta = safeObj(next.insightsMeta);
  const tagsMeta = safeObj(next.tagsMeta);

  const insightsDerived = normalizeVersion(insightsMeta.derivedFromResearchVersion, researchVersion);
  const tagsDerived = normalizeVersion(tagsMeta.derivedFromResearchVersion, researchVersion);

  next.researchVersion = researchVersion;
  next.insightsVersion = insightsVersion;
  next.tagsVersion = tagsVersion;
  next.insightsMeta = { ...insightsMeta, derivedFromResearchVersion: insightsDerived };
  next.tagsMeta = { ...tagsMeta, derivedFromResearchVersion: tagsDerived };

  if (next.dismissedForResearchVersion == null) next.dismissedForResearchVersion = null;
  const dismissedDecisionStaleSignature = safeText(next.dismissedDecisionStaleSignature).trim();
  next.dismissedDecisionStaleSignature = dismissedDecisionStaleSignature || null;
  const dismissedArtifactFreshnessByInsightsVersion = safeObj(next.dismissedArtifactFreshnessByInsightsVersion);
  const normalizedDismissedByArtifact = {};
  Object.entries(dismissedArtifactFreshnessByInsightsVersion).forEach(([artifactType, version]) => {
    const normalized = normalizeVersion(version, -1);
    if (normalized >= 0) normalizedDismissedByArtifact[String(artifactType)] = normalized;
  });
  next.dismissedArtifactFreshnessByInsightsVersion = normalizedDismissedByArtifact;

  return next;
}

function normalizeDirection(artifact) {
  const t = now();
  const a = safeObj(artifact);

  const normalizeDraft = (d) => {
    if (!d || typeof d !== "object") return null;
    const dd = { ...d };
    if (typeof dd.mode !== "string") dd.mode = "draft";
    if (typeof dd.label !== "string") dd.label = "Draft focus (needs review)";
    if (typeof dd.anchor !== "string") dd.anchor = "";
    if (typeof dd.constraint !== "string") dd.constraint = "";
    if (!Array.isArray(dd.implications)) dd.implications = [];
    if (!Array.isArray(dd.assumptionsToValidate)) dd.assumptionsToValidate = [];
    if (!Array.isArray(dd.successSignals)) dd.successSignals = [];
    if (dd.source && typeof dd.source === "object") dd.source = safeObj(dd.source);
    else if (dd.source == null) dd.source = null;
    if (!Number.isFinite(Number(dd.generatedAt)) && dd.generatedAt != null) dd.generatedAt = null;
    return dd;
  };

  const normalizeFinal = (f) => {
    if (!f || typeof f !== "object") return null;
    const ff = { ...f };
    if (typeof ff.mode !== "string") ff.mode = "final";
    if (typeof ff.label !== "string") ff.label = "Direction";
    if (typeof ff.anchor !== "string") ff.anchor = "";
    if (typeof ff.constraint !== "string") ff.constraint = "";
    if (!Array.isArray(ff.implications)) ff.implications = [];
    if (!Array.isArray(ff.assumptionsToValidate)) ff.assumptionsToValidate = [];
    if (!Array.isArray(ff.successSignals)) ff.successSignals = [];
    if (ff.source && typeof ff.source === "object") ff.source = safeObj(ff.source);
    else if (ff.source == null) ff.source = null;
    if (!Number.isFinite(Number(ff.updatedAt)) && ff.updatedAt != null) ff.updatedAt = null;
    return ff;
  };

  return {
    draft: normalizeDraft(isPlainObject(a.draft) ? a.draft : null),
    final: normalizeFinal(isPlainObject(a.final) ? a.final : null),
    updatedAt: Number.isFinite(Number(a.updatedAt)) ? Number(a.updatedAt) : t,
  };
}

function normalizeJTBDs(raw) {
  const t = now();
  return (Array.isArray(raw) ? raw : [])
    .map((item, idx) => {
      const jtbd = safeObj(item);
      const originRaw = typeof jtbd.origin === "string" ? jtbd.origin.trim().toLowerCase() : "";
      const origin = originRaw === "user" || originRaw === "manual" ? "user" : "ai";
      const isEdited = toBool(jtbd.isEdited ?? false) || originRaw === "edited";
      return {
        id: safeText(jtbd.id).trim() || `jtbd_${idx + 1}`,
        title: safeText(jtbd.title).trim() || "Jobs to be done",
        mainJob: safeText(jtbd.mainJob).trim(),
        situation: safeText(jtbd.situation).trim(),
        motivation: safeText(jtbd.motivation).trim(),
        desiredOutcome: safeText(jtbd.desiredOutcome).trim(),
        barriers: normalizeStringArray(jtbd.barriers),
        alternatives: normalizeStringArray(jtbd.alternatives),
        successCriteria: normalizeStringArray(jtbd.successCriteria),
        constraints: normalizeStringArray(jtbd.constraints),
        origin,
        isEdited,
        audienceGroupId: safeText(jtbd.audienceGroupId).trim() || null,
        audienceGroupName: safeText(jtbd.audienceGroupName).trim(),
        audienceGroupDescription: safeText(jtbd.audienceGroupDescription).trim(),
        createdAt: Number.isFinite(Number(jtbd.createdAt)) ? Number(jtbd.createdAt) : t,
        updatedAt: Number.isFinite(Number(jtbd.updatedAt)) ? Number(jtbd.updatedAt) : t,
        lastEditedAt: Number.isFinite(Number(jtbd.lastEditedAt)) ? Number(jtbd.lastEditedAt) : null,
        version: Number.isFinite(Number(jtbd.version)) ? Number(jtbd.version) : 0,
      };
    })
    .filter((jtbd) => jtbd.title || jtbd.mainJob);
}

function normalizeProblemStatements(raw) {
  const t = now();
  return (Array.isArray(raw) ? raw : [])
    .map((item, idx) => {
      const statement = safeObj(item);
      const originRaw =
        typeof statement.origin === "string" ? statement.origin.trim().toLowerCase() : "";
      const origin = originRaw === "user" || originRaw === "manual" ? "user" : "ai";
      const isEdited = toBool(statement.isEdited ?? false) || originRaw === "edited";
      return {
        id: safeText(statement.id).trim() || `ps_${idx + 1}`,
        title: safeText(statement.title).trim() || "Problem statement",
        coreProblem: safeText(statement.coreProblem).trim(),
        affectedUsers: safeText(statement.affectedUsers).trim(),
        whyItMatters: safeText(statement.whyItMatters).trim(),
        contributingFactors: normalizeStringArray(statement.contributingFactors),
        keySignals: normalizeStringArray(statement.keySignals),
        linkedInsightIds: normalizeStringArray(statement.linkedInsightIds),
        opportunityAreas: (Array.isArray(statement.opportunityAreas) ? statement.opportunityAreas : [])
          .map((area) => {
            if (!area || typeof area !== "object") return null;
            const title = safeText(area.title).trim();
            const description = safeText(area.description).trim();
            if (!title) return null;
            return { title, description };
          })
          .filter(Boolean),
        origin,
        isEdited,
        audienceGroupId: safeText(statement.audienceGroupId).trim() || null,
        audienceGroupName: safeText(statement.audienceGroupName).trim(),
        audienceGroupDescription: safeText(statement.audienceGroupDescription).trim(),
        approvedInsightsVersion: Number.isFinite(Number(statement.approvedInsightsVersion))
          ? Number(statement.approvedInsightsVersion)
          : null,
        createdAt: Number.isFinite(Number(statement.createdAt)) ? Number(statement.createdAt) : t,
        updatedAt: Number.isFinite(Number(statement.updatedAt)) ? Number(statement.updatedAt) : t,
        lastEditedAt: Number.isFinite(Number(statement.lastEditedAt))
          ? Number(statement.lastEditedAt)
          : null,
        version: Number.isFinite(Number(statement.version)) ? Number(statement.version) : 0,
      };
    })
    .filter(
      (statement) =>
        statement.title ||
        statement.coreProblem ||
        statement.affectedUsers ||
        statement.whyItMatters
    );
}

const STORY_STYLE_LEGACY_MAP = Object.freeze({
  clean_editorial: "editorial",
  soft_illustration: "softColor",
  storyboard_sketch: "sketch",
});

function normalizeStoryStyleValue(value) {
  const raw = safeText(value).trim();
  const lower = raw.toLowerCase();
  if (lower === "editorial") return "editorial";
  if (lower === "minimal") return "minimal";
  if (lower === "softcolor" || lower === "soft_color") return "softColor";
  if (lower === "sketch") return "sketch";
  if (lower === "narrativeminimal" || lower === "narrative_minimal" || lower === "narrative-minimal") {
    return "narrativeMinimal";
  }
  return STORY_STYLE_LEGACY_MAP[lower] || "editorial";
}

export function normalizeStoryArtifacts(raw) {
  const t = now();
  const VALID_TYPES = new Set(["storyboard", "presentation", "journey"]);
  const safeItems = Array.isArray(raw) ? raw : [];

  return safeItems
    .map((artifact, idx) => {
      const item = safeObj(artifact);
      const typeRaw = safeText(item.type).trim().toLowerCase();
      const type = VALID_TYPES.has(typeRaw) ? typeRaw : "storyboard";
      const defaultStyle = "editorial";
      const visualStyle = normalizeStoryStyleValue(item.visualStyle) || defaultStyle;
      const items = (Array.isArray(item.items) ? item.items : [])
        .map((entry, itemIdx) => {
          const row = safeObj(entry);
          const title = safeText(row.title || row.headline || row.stageName).trim();
          const body = safeText(row.body || row.caption || row.summary || row.supportingText).trim();
          if (!title || !body) return null;
          const originRaw = safeText(row.origin).trim().toLowerCase();
          const origin = originRaw === "manual" ? "manual" : "ai";
          return {
            id: safeText(row.id).trim() || `story_item_${idx + 1}_${itemIdx + 1}`,
            type:
              safeText(row.type).trim() ||
              (type === "presentation" ? "slide" : type === "journey" ? "step" : "frame"),
            title,
            body,
            imagePrompt: safeText(row.imagePrompt || row.visualPrompt).trim(),
            imageFinalPrompt: safeText(row.imageFinalPrompt || row.generatedPrompt).trim(),
            imageUrl: safeText(row.imageUrl).trim() || null,
            imageStatus: (() => {
              const status = safeText(row.imageStatus).trim().toLowerCase();
              if (["idle", "generating", "generated", "failed", "placeholder"].includes(status)) return status;
              return safeText(row.imageUrl).trim() ? "generated" : "idle";
            })(),
            imageProvider: safeText(row.imageProvider).trim() || null,
            imageError: safeText(row.imageError).trim() || null,
            imageGeneratedAt: Number.isFinite(Number(row.imageGeneratedAt))
              ? Number(row.imageGeneratedAt)
              : null,
            linkedInsightIds: normalizeStringArray(row.linkedInsightIds),
            linkedInsightTitles: normalizeStringArray(row.linkedInsightTitles),
            origin,
            isEdited: toBool(row.isEdited),
            marker: safeText(row.marker || row.tensionMarker || row.opportunityMarker).trim(),
            position: Number.isFinite(Number(row.position)) ? Number(row.position) : itemIdx,
          };
        })
        .filter(Boolean)
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
        .map((entry, itemIdx) => ({ ...entry, position: itemIdx }));

      const sourceRaw = safeObj(item.source);
      const source = {
        approvedInsightsVersion: Number.isFinite(Number(sourceRaw.approvedInsightsVersion))
          ? Number(sourceRaw.approvedInsightsVersion)
          : null,
        approvedInsightIds: normalizeStringArray(sourceRaw.approvedInsightIds),
        overviewSnapshotVersion: Number.isFinite(Number(sourceRaw.overviewSnapshotVersion))
          ? Number(sourceRaw.overviewSnapshotVersion)
          : null,
        decisionsVersion: Number.isFinite(Number(sourceRaw.decisionsVersion))
          ? Number(sourceRaw.decisionsVersion)
          : null,
      };

      const hasManualItems = items.some((entry) => entry.origin === "manual");
      const hasEditedItems = items.some((entry) => entry.isEdited);
      const manualOnly = items.length > 0 && items.every((entry) => entry.origin === "manual");
      const originRaw = safeText(item.origin).trim().toLowerCase();
      const derivedOrigin = manualOnly ? "manual" : hasManualItems || hasEditedItems ? "mixed" : "ai";
      const origin =
        originRaw === "manual" || originRaw === "mixed" || originRaw === "ai"
          ? originRaw
          : derivedOrigin;

      return {
        id: safeText(item.id).trim() || `story_${idx + 1}`,
        type,
        title: safeText(item.title).trim() || "Story artifact",
        summary: safeText(item.summary).trim(),
        visualStyle,
        origin,
        isEdited: toBool(item.isEdited),
        createdAt: Number.isFinite(Number(item.createdAt)) ? Number(item.createdAt) : t,
        updatedAt: Number.isFinite(Number(item.updatedAt)) ? Number(item.updatedAt) : t,
        source,
        items,
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
}

/* ------------------------------------------------------------------ */
/* Canonical containers                                                */
/* ------------------------------------------------------------------ */

export const INIT_ARTIFACT_KEYS = [
  "overview",
  "problemStatements",
  "jtbds",
  "storyArtifacts",
  "journeys",
  "journeyMap",
  "interviewQuestions",
  "interviewScripts",
  "researchInsights",
  "researchActorIds",
  "atomicFacts",
  "problemDefinition",
  "affinityClusters",
  "businessModelCanvas",
  "benchmarking",
  "valuePropositionCanvas",
  "userFlows",
  "direction",
  "researchTagSpans",
  "insightGroups",
  "tagVocabulary",
  "tagThemes",
  "storyStyle",
];

function safeLowerTag(v) {
  return String(v == null ? "" : v)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeResearchTagSpans(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const itemsRaw = Array.isArray(raw?.items) ? raw.items : [];

  const items = itemsRaw
    .map((x, idx) => {
      const o = x && typeof x === "object" ? x : null;
      if (!o) return null;
      const tag = safeLowerTag(o.tag);
      if (!tag) return null;

      const researchItemId = String(o.researchItemId || "").trim();
      if (!researchItemId) return null;

      const start = Number(o.start);
      const end = Number(o.end);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end <= start) return null;

      const id = String(o.id || "").trim() || `tag_${researchItemId}_${start}_${end}_${idx}`;
      const context = typeof o.context === "string" ? o.context : undefined;
      const createdAt = Number.isFinite(Number(o.createdAt)) ? Number(o.createdAt) : t;
      const updatedAt = Number.isFinite(Number(o.updatedAt)) ? Number(o.updatedAt) : createdAt;

      return { id, tag, researchItemId, start, end, ...(context ? { context } : {}), createdAt, updatedAt };
    })
    .filter(Boolean);

  const seen = new Set();
  const deduped = [];
  for (const it of items) {
    const k = `${it.tag}::${it.researchItemId}::${it.start}::${it.end}`;
    if (seen.has(k)) continue;
    seen.add(k);
    deduped.push(it);
  }

  return {
    version: Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1,
    items: deduped,
    createdAt: Number.isFinite(Number(raw?.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t,
  };
}

function normalizeInitiativeNotes(list, initiativeId) {
  const items = Array.isArray(list) ? list : [];
  const tIso = isoNow();

  return items
    .map((note, idx) => {
      const raw = note && typeof note === "object" ? note : null;
      if (!raw) return null;

      const researchItemId =
        typeof raw.researchItemId === "string" && raw.researchItemId.trim()
          ? raw.researchItemId.trim()
          : "";
      if (!researchItemId) return null;

      const startOffset = Number(raw.startOffset);
      const endOffset = Number(raw.endOffset);
      if (!Number.isFinite(startOffset) || !Number.isFinite(endOffset) || endOffset <= startOffset) return null;

      const status = raw.status === "resolved" ? "resolved" : "open";
      const createdAt =
        typeof raw.createdAt === "string" && raw.createdAt.trim() ? raw.createdAt.trim() : tIso;
      const updatedAt =
        typeof raw.updatedAt === "string" && raw.updatedAt.trim() ? raw.updatedAt.trim() : createdAt;

      const replies = Array.isArray(raw.replies)
        ? raw.replies
            .map((reply, rIdx) => {
              const r = reply && typeof reply === "object" ? reply : null;
              if (!r) return null;
              const message = typeof r.message === "string" ? r.message.trim() : "";
              if (!message) return null;
              return {
                id:
                  typeof r.id === "string" && r.id.trim()
                    ? r.id.trim()
                    : `reply_${researchItemId}_${rIdx + 1}`,
                message,
                createdBy:
                  typeof r.createdBy === "string" && r.createdBy.trim()
                    ? r.createdBy.trim()
                    : "local-user",
                createdAt:
                  typeof r.createdAt === "string" && r.createdAt.trim()
                    ? r.createdAt.trim()
                    : tIso,
              };
            })
            .filter(Boolean)
        : [];

      return {
        id:
          typeof raw.id === "string" && raw.id.trim()
            ? raw.id.trim()
            : `note_${initiativeId || "init"}_${idx + 1}`,
        researchItemId,
        initiativeId:
          typeof raw.initiativeId === "string" && raw.initiativeId.trim()
            ? raw.initiativeId.trim()
            : initiativeId || null,
        startOffset,
        endOffset,
        selectedText: typeof raw.selectedText === "string" ? raw.selectedText : "",
        message: typeof raw.message === "string" ? raw.message.trim() : "",
        status,
        createdBy:
          typeof raw.createdBy === "string" && raw.createdBy.trim()
            ? raw.createdBy.trim()
            : "local-user",
        createdAt,
        updatedBy:
          typeof raw.updatedBy === "string" && raw.updatedBy.trim()
            ? raw.updatedBy.trim()
            : "local-user",
        updatedAt,
        replies,
      };
    })
    .filter(Boolean);
}

function normalizeTagVocabulary(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const itemsRaw = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.tags)
      ? raw.tags
      : [];

  const items = [];
  const seen = new Set();

  for (const x of itemsRaw) {
    const rawTag =
      typeof x === "string"
        ? x
        : x && typeof x === "object"
          ? x.tag ?? x.name ?? x.label ?? ""
          : "";
    const tag = safeLowerTag(rawTag);
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    const description =
      x && typeof x === "object" ? String(x.description ?? x.desc ?? "").trim() : "";
    items.push({ tag, description });
  }

  return {
    version: Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1,
    items,
    createdAt: Number.isFinite(Number(raw?.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t,
  };
}

function normalizeTagThemes(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const themesRaw = Array.isArray(raw?.themes) ? raw.themes : [];
  const overridesRaw = raw?.overrides && typeof raw.overrides === "object" ? raw.overrides : {};

  const themes = themesRaw
    .map((theme, idx) => {
      if (!theme || typeof theme !== "object") return null;
      const title = String(theme.title ?? theme.name ?? "").trim() || `Theme ${idx + 1}`;
      const rawId = String(theme.id || "").trim();
      const id = rawId || `theme_${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
      const order = Number.isFinite(Number(theme.order)) ? Number(theme.order) : idx;
      const origin = theme.origin === "user" ? "user" : theme.origin === "system" ? "system" : "ai";

      const tagsRaw = Array.isArray(theme.tags)
        ? theme.tags
        : typeof theme.tags === "string"
          ? theme.tags.split(",")
          : [];

      const tags = [];
      const seen = new Set();
      tagsRaw.forEach((tg) => {
        const tag = safeLowerTag(tg);
        if (!tag || seen.has(tag)) return;
        seen.add(tag);
        tags.push(tag);
      });

      const createdAt = Number.isFinite(Number(theme.createdAt)) ? Number(theme.createdAt) : t;
      const updatedAt = Number.isFinite(Number(theme.updatedAt)) ? Number(theme.updatedAt) : createdAt;

      return { id, title, order, tags, origin, createdAt, updatedAt };
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order);

  const tagToThemeIdRaw =
    overridesRaw.tagToThemeId && typeof overridesRaw.tagToThemeId === "object"
      ? overridesRaw.tagToThemeId
      : {};
  const themeTitlesRaw =
    overridesRaw.themeTitles && typeof overridesRaw.themeTitles === "object"
      ? overridesRaw.themeTitles
      : {};

  const tagToThemeId = {};
  Object.entries(tagToThemeIdRaw).forEach(([tag, themeId]) => {
    const tKey = safeLowerTag(tag);
    const tVal = String(themeId || "").trim();
    if (tKey && tVal) tagToThemeId[tKey] = tVal;
  });

  const themeTitles = {};
  Object.entries(themeTitlesRaw).forEach(([themeId, title]) => {
    const id = String(themeId || "").trim();
    const tTitle = String(title || "").trim();
    if (id && tTitle) themeTitles[id] = tTitle;
  });

  return {
    version: Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1,
    themes,
    overrides: { tagToThemeId, themeTitles },
    createdAt: Number.isFinite(Number(raw?.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t,
  };
}

function normalizeInsightGroups(container) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const version = Number.isFinite(Number(raw?.version)) ? Number(raw.version) : 1;
  const groupsRaw = Array.isArray(raw?.groups) ? raw.groups : [];
  const allowedStatuses = new Set(["draft", "needs_review", "approved"]);
  const isGenericGroupId = (id) => {
    const rawId = safeText(id).trim().toLowerCase();
    if (!rawId) return true;
    const stripped = rawId.replace(/^grp_/, "");
    return !stripped || /^grp?\d+$/.test(stripped) || /^group_?\d+$/.test(stripped);
  };
  const slugToGroupId = (value, fallbackIndex = 0) => {
    const base = safeText(value)
      .toLowerCase()
      .trim()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    const safeBase = base || `group_${fallbackIndex + 1}`;
    return `grp_${safeBase}`;
  };

  const groups = groupsRaw
    .map((g, idx) => {
      if (!g || typeof g !== "object") return null;
      const baseTitle = typeof g.title === "string" && g.title.trim() ? g.title.trim() : "Ungrouped insights";
      const rawId = typeof g.id === "string" && g.id.trim() ? g.id.trim() : "";
      const id =
        rawId && !isGenericGroupId(rawId)
          ? rawId.trim()
          : slugToGroupId(baseTitle, idx);
      const order = Number.isFinite(Number(g.order)) ? Number(g.order) : idx;
      const summary = typeof g.summary === "string" ? g.summary.trim() : "";
      const notes = typeof g.notes === "string" ? g.notes : "";
      const statusRaw = typeof g.status === "string" ? g.status.trim().toLowerCase() : "";
      const status = allowedStatuses.has(statusRaw) ? statusRaw : "draft";
      const createdAt = Number.isFinite(Number(g.createdAt)) ? Number(g.createdAt) : t;
      const updatedAt = Number.isFinite(Number(g.updatedAt)) ? Number(g.updatedAt) : createdAt;
      return { id, title: baseTitle, summary, notes, status, order, createdAt, updatedAt };
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order);

  if (!groups.length) {
    groups.push({ id: "grp_ungrouped", title: "Ungrouped insights", order: 0, createdAt: t, updatedAt: t });
  }

  return {
    version,
    groups,
    createdAt: Number.isFinite(Number(raw?.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t,
  };
}

function normalizeInsightsSet(container, researchItemCount, fallbackInsights = null, fallbackGroups = null) {
  const t = now();
  const raw = container && typeof container === "object" ? container : null;
  const researchInsights = raw?.researchInsights
    ? normalizeResearchInsights(raw.researchInsights, researchItemCount)
    : fallbackInsights;
  const insightGroups = raw?.insightGroups ? normalizeInsightGroups(raw.insightGroups) : fallbackGroups;
  return {
    researchInsights:
      researchInsights ||
      { version: 1, items: [], createdAt: t, updatedAt: t, reviewStatus: "draft", reviewedAt: null, emergingSignals: { items: [] } },
    insightGroups: insightGroups || normalizeInsightGroups(null),
    updatedAt: Number.isFinite(Number(raw?.updatedAt)) ? Number(raw.updatedAt) : t,
  };
}

function normalizeManualMapping(container) {
  const raw = container && typeof container === "object" ? container : {};
  const draftGroupsRaw = Array.isArray(raw.draftGroups) ? raw.draftGroups : [];
  const draftGroups = draftGroupsRaw
    .map((g, idx) => {
      if (!g || typeof g !== "object") return null;
      const id = typeof g.id === "string" && g.id.trim() ? g.id.trim() : `mm_grp_${idx + 1}`;
      const title = typeof g.title === "string" && g.title.trim() ? g.title.trim() : `Group ${idx + 1}`;
      const atomicFactIds = Array.isArray(g.atomicFactIds)
        ? Array.from(
            new Set(
              g.atomicFactIds.map((id) => (typeof id === "string" ? id.trim() : "")).filter(Boolean)
            )
          )
        : [];
      return { id, title, atomicFactIds };
    })
    .filter(Boolean);

  const unassignedAtomicFactIds = Array.isArray(raw.unassignedAtomicFactIds)
    ? Array.from(
        new Set(
          raw.unassignedAtomicFactIds
            .map((id) => (typeof id === "string" ? id.trim() : ""))
            .filter(Boolean)
        )
      )
    : [];

  return {
    isActive: raw.isActive === true,
    draftGroups,
    unassignedAtomicFactIds,
    startedAt: typeof raw.startedAt === "string" && raw.startedAt.trim() ? raw.startedAt.trim() : null,
    lastEditedAt: typeof raw.lastEditedAt === "string" && raw.lastEditedAt.trim() ? raw.lastEditedAt.trim() : null,
  };
}

function syncInsightGroupsWithThemes(insightGroups, insights, tagThemes) {
  const t = now();
  const cont = normalizeInsightGroups(insightGroups);
  const groupById = new Map(cont.groups.map((g) => [g.id, g]));
  const titleToGroupId = new Map(
    cont.groups.map((g) => [
      String(g.title || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim(),
      g.id,
    ])
  );

  const themeItems = Array.isArray(tagThemes?.themes) ? tagThemes.themes : [];
  const themeTagToGroupId = new Map();

  themeItems.forEach((th) => {
    const themeId =
      typeof th?.id === "string" && th.id.trim()
        ? th.id.trim()
        : `grp_${String(th?.title || "theme").toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
    const themeTitle =
      typeof th?.title === "string" && th.title.trim()
        ? th.title.trim()
        : themeId.replace(/^grp_/, "").replace(/_/g, " ");
    if (!groupById.has(themeId)) {
      groupById.set(themeId, {
        id: themeId,
        title: themeTitle,
        order: groupById.size,
        createdAt: t,
        updatedAt: t,
      });
    }
    (Array.isArray(th?.tags) ? th.tags : []).forEach((tag) => {
      const key = safeLowerTag(tag);
      if (key && !themeTagToGroupId.has(key)) themeTagToGroupId.set(key, themeId);
    });
  });

  const nextItems = (Array.isArray(insights) ? insights : []).map((it) => {
    const baseGroupId =
      typeof it?.groupId === "string" && it.groupId.trim() ? it.groupId.trim() : "grp_ungrouped";
    let gid = baseGroupId;

    // Preserve explicit AI/manual grouping. Only derive from themes for currently ungrouped insights.
    if (gid === "grp_ungrouped" && Array.isArray(it?.tags) && it.tags.length) {
      for (const rawTag of it.tags) {
        const tagKey = safeLowerTag(rawTag);
        if (!tagKey) continue;
        const mappedTheme = themeTagToGroupId.get(tagKey);
        if (mappedTheme) {
          gid = mappedTheme;
          break;
        }
      }
    }

    if (gid === "grp_ungrouped" && Array.isArray(it?.tags) && it.tags.length) {
      const tagKey = String(it.tags[0] || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
      const mapped = titleToGroupId.get(tagKey);
      if (mapped) gid = mapped;
    }

    if (!groupById.has(gid)) {
      groupById.set(gid, {
        id: gid,
        title:
          gid === "grp_ungrouped"
            ? "Ungrouped insights"
            : gid.replace(/^grp_/, "").replace(/_/g, " "),
        order: groupById.size,
        createdAt: t,
        updatedAt: t,
      });
    }

    return gid === it.groupId ? it : { ...it, groupId: gid };
  });

  const usedGroupIds = new Set(nextItems.map((it) => it.groupId || "grp_ungrouped"));
  // Preserve existing groups so newly created empty themes stay visible.
  cont.groups.forEach((g) => {
    if (g?.id) usedGroupIds.add(g.id);
  });
  usedGroupIds.add("grp_ungrouped");

  const groups = Array.from(groupById.values())
    .filter((g) => usedGroupIds.has(g.id))
    .map((g, idx) => ({
      ...g,
      order: Number.isFinite(Number(g.order)) ? Number(g.order) : idx,
    }));

  return { insightGroups: { ...cont, groups, updatedAt: t }, items: nextItems };
}

function normalizeEvidenceItems(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  return arr
    .map((e, idx) => {
      const o = e && typeof e === "object" ? e : null;
      if (!o) return null;

      const text = typeof o.text === "string" ? o.text.trim() : "";
      if (!text) return null;

      const typeRaw = typeof o.type === "string" ? o.type.trim() : "";
      const type = typeRaw === "quote" || typeRaw === "observation" || typeRaw === "fact" ? typeRaw : "observation";

      const researchItemId =
        typeof o.researchItemId === "string" && o.researchItemId.trim() ? o.researchItemId.trim() : null;

      const start = Number.isFinite(Number(o.start)) ? Number(o.start) : undefined;
      const end = Number.isFinite(Number(o.end)) ? Number(o.end) : undefined;

      const key = [
        text,
        type,
        researchItemId || "",
        Number.isFinite(start) ? String(start) : "",
        Number.isFinite(end) ? String(end) : "",
      ]
        .filter(Boolean)
        .join("::");
      if (key && seen.has(key)) return null;
      if (key) seen.add(key);

      const id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : `ev_${key || idx + 1}`;

      return {
        id,
        text,
        type,
        researchItemId,
        ...(start != null ? { start } : {}),
        ...(end != null ? { end } : {}),
      };
    })
    .filter(Boolean);
}

function computeEvidenceStrength(evidence) {
  const arr = Array.isArray(evidence) ? evidence : [];
  const count = arr.length;
  if (!count) return "none";
  const sources = new Set(arr.map((e) => e?.researchItemId).filter(Boolean));
  const sourceCount = sources.size;
  if (sourceCount >= 3 || count >= 4) return "high";
  if (sourceCount >= 2 || (count >= 2 && count <= 3)) return "medium";
  return "low";
}

function computeNeedsEvidence(evidence) {
  const strength = computeEvidenceStrength(evidence);
  return strength === "none" || strength === "low";
}

function filterEvidenceForResearch(evidence, researchItemCount) {
  const arr = Array.isArray(evidence) ? evidence : [];
  return arr.filter((e) => e && typeof e.text === "string" && e.text.trim());
}

function deriveInsightTitleFromText(text, { maxWords = 8, maxChars = 80 } = {}) {
  const raw = safeText(text).replace(/[“”"]/g, "").trim();
  if (!raw) return "Untitled insight";
  const sentence = raw.split(/(?<=[.!?])\s+/)[0] || raw;
  const words = sentence.split(/\s+/).filter(Boolean).slice(0, maxWords);
  let title = words.join(" ");
  if (title.length > maxChars) title = title.slice(0, maxChars).trim();
  return title || "Untitled insight";
}

function normalizeInsightItem(it, idx, researchItemCount) {
  const t = now();
  const raw = it && typeof it === "object" ? it : {};
  const text = typeof raw.text === "string" ? raw.text.trim() : "";
  const titleRaw = typeof raw.title === "string" ? raw.title.trim() : "";
  const title = titleRaw || deriveInsightTitleFromText(text);
  if (!text) return null;
  const sourceTypeRaw =
    typeof raw.sourceType === "string"
      ? raw.sourceType.trim().toLowerCase()
      : typeof raw.source?.type === "string"
        ? raw.source.type.trim().toLowerCase()
        : "";
  const sourceType = ["research", "prompt", "ai_suggested"].includes(sourceTypeRaw)
    ? sourceTypeRaw
    : undefined;
  const sourceRaw =
    typeof raw.source === "string"
      ? raw.source.trim().toLowerCase()
      : typeof raw.source?.type === "string"
        ? raw.source.type.trim().toLowerCase()
        : "";
  const source = sourceRaw === "hypothesis" ? "hypothesis" : undefined;

  const tags = Array.isArray(raw.tags)
    ? raw.tags.filter((x) => typeof x === "string" && x.trim()).map((x) => x.trim())
    : [];

  const groupId = typeof raw.groupId === "string" && raw.groupId.trim() ? raw.groupId.trim() : "grp_ungrouped";

  const reviewStatusRaw = typeof raw.reviewStatus === "string" ? raw.reviewStatus : "ai_generated";
  const reviewStatus =
    reviewStatusRaw === "reviewed" || reviewStatusRaw === "approved" || reviewStatusRaw === "edited"
      ? reviewStatusRaw
      : "ai_generated";

  const provenanceRaw = isPlainObject(raw.provenance) ? raw.provenance : {};
  const createdAt = Number.isFinite(Number(provenanceRaw.createdAt)) ? Number(provenanceRaw.createdAt) : t;
  const updatedAt = Number.isFinite(Number(provenanceRaw.updatedAt)) ? Number(provenanceRaw.updatedAt) : createdAt;

  const provenance = {
    createdBy: provenanceRaw.createdBy === "human" ? "human" : "ai",
    lastEditedBy: provenanceRaw.lastEditedBy === "human" ? "human" : provenanceRaw.lastEditedBy === "ai" ? "ai" : "ai",
    createdAt,
    updatedAt,
    ...(provenanceRaw.modelRunId ? { modelRunId: provenanceRaw.modelRunId } : {}),
  };

  const fallbackCreatedBy = provenance?.createdBy === "ai" ? "ai" : "local-user";
  const createdBy =
    typeof raw.createdBy === "string" && raw.createdBy.trim() ? raw.createdBy.trim() : fallbackCreatedBy;
  const updatedBy =
    typeof raw.updatedBy === "string" && raw.updatedBy.trim() ? raw.updatedBy.trim() : createdBy;
  const updatedAtIso = typeof raw.updatedAt === "string" && raw.updatedAt.trim() ? raw.updatedAt.trim() : null;

  const rawEvidence = normalizeEvidenceItems(raw.evidence);
  const evidence = filterEvidenceForResearch(rawEvidence, researchItemCount);
  const evidenceStrength = computeEvidenceStrength(evidence);

  const needsEvidence =
    typeof raw.needsEvidence === "boolean" ? raw.needsEvidence : computeNeedsEvidence(evidence);
  const factIds = Array.isArray(raw.factIds)
    ? Array.from(
        new Set(
          raw.factIds
            .map((id) => String(id || "").trim())
            .filter(Boolean)
        )
      )
    : [];

  return {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : `ins_${idx + 1}`,
    title,
    text,
    ...(tags.length ? { tags } : {}),
    ...(groupId ? { groupId } : {}),
    ...(Number.isFinite(Number(raw.order)) ? { order: Number(raw.order) } : { order: idx }),
    reviewStatus,
    provenance,
    createdBy,
    updatedBy,
    ...(updatedAtIso ? { updatedAt: updatedAtIso } : {}),
    needsEvidence,
    evidenceStrength,
    evidence,
    ...(factIds.length ? { factIds } : {}),
    ...(sourceType ? { sourceType } : {}),
    ...(source ? { source } : {}),
  };
}

function normalizeResearchInsights(container, researchItemCount) {
  const t = now();
  const raw = container && typeof container === "object" ? container : {};
  const itemsRaw = Array.isArray(raw.items) ? raw.items : [];
  const items = itemsRaw.map((it, idx) => normalizeInsightItem(it, idx, researchItemCount)).filter(Boolean);
  const reviewStatusRaw = typeof raw.reviewStatus === "string" ? raw.reviewStatus.trim().toLowerCase() : "";
  const isReviewed = toBool(raw.isReviewed) || reviewStatusRaw === "reviewed";
  const reviewStatus = isReviewed ? "reviewed" : "draft";
  const reviewedAt = Number.isFinite(Number(raw.reviewedAt)) ? Number(raw.reviewedAt) : null;
  const emergingSignals =
    Array.isArray(raw.emergingSignals)
      ? { items: raw.emergingSignals }
      : raw.emergingSignals && typeof raw.emergingSignals === "object"
        ? raw.emergingSignals
        : { items: [] };

  return {
    version: Number.isFinite(Number(raw.version)) ? Number(raw.version) : 1,
    items,
    createdAt: Number.isFinite(Number(raw.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw.updatedAt)) ? Number(raw.updatedAt) : t,
    reviewStatus,
    reviewedAt,
    emergingSignals,
  };
}

export function normalizeAtomicFacts(container, researchItemCount) {
  const t = now();
  const raw = container && typeof container === "object" ? container : {};
  const itemsRaw = Array.isArray(raw.items) ? raw.items : [];
  const items = itemsRaw
    .map((it, idx) => {
      const o = it && typeof it === "object" ? it : {};
      const text = typeof o.text === "string" ? o.text.trim() : "";
      if (!text) return null;
      const tags = Array.isArray(o.tags)
        ? o.tags.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean)
        : [];
      if (!tags.length) return null;
      const evidence = filterEvidenceForResearch(normalizeEvidenceItems(o.evidence), researchItemCount);
      if (!evidence.length) return null;
      const id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : `fact_${idx + 1}`;
      const type =
        typeof o.type === "string" && o.type.trim() ? o.type.trim() : "requirement";
      return { id, text, type, tags, evidence };
    })
    .filter(Boolean);

  return {
    version: Number.isFinite(Number(raw.version)) ? Number(raw.version) : 1,
    items,
    createdAt: Number.isFinite(Number(raw.createdAt)) ? Number(raw.createdAt) : t,
    updatedAt: Number.isFinite(Number(raw.updatedAt)) ? Number(raw.updatedAt) : t,
  };
}

function normalizeInterviewQuestionsMap(rawMap, personas) {
  const iq = safeObj(rawMap);
  const nextIQ = {};

  Object.keys(iq).forEach((personaId) => {
    const slot = iq[personaId];
    const entry = Array.isArray(slot)
      ? { items: slot, _meta: {} }
      : isPlainObject(slot)
        ? slot
        : { items: [], _meta: {} };

    const items = Array.isArray(entry.items) ? entry.items : [];
    const meta = safeObj(entry._meta);
    const persona = (Array.isArray(personas) ? personas : []).find((p) => p.id === personaId);
    const personaVersion = typeof persona?.version === "number" ? persona.version : 0;

    nextIQ[personaId] = {
      items,
      _meta: {
        generatedAt: meta.generatedAt ?? now(),
        source: meta.source ?? "unknown",
        personaId,
        personaVersion: meta.personaVersion ?? personaVersion,
        customCategories: Array.isArray(meta.customCategories) ? meta.customCategories : [],
        isReviewed: toBool(meta.isReviewed ?? false),
        reviewedAt: meta.reviewedAt ?? null,
      },
    };
  });

  return nextIQ;
}

const RESEARCH_SCRIPT_TYPES = new Set(["interview", "survey", "usability_test"]);
const RESEARCH_SCRIPT_STATUSES = new Set(["draft", "reviewed"]);

function normalizeResearchScriptType(type) {
  const raw = safeText(type).trim().toLowerCase();
  return RESEARCH_SCRIPT_TYPES.has(raw) ? raw : "interview";
}

function normalizeResearchScriptStatus(status) {
  const raw = safeText(status).trim().toLowerCase();
  return RESEARCH_SCRIPT_STATUSES.has(raw) ? raw : "draft";
}

function defaultResearchScriptTitle(type, questionText) {
  const q = safeText(questionText).trim();
  if (q) {
    const short = q.length > 70 ? `${q.slice(0, 70).trim()}...` : q;
    const typeLabel = type === "survey" ? "Survey" : type === "usability_test" ? "Usability test" : "Interview";
    return `${typeLabel}: ${short}`;
  }
  return type === "survey" ? "Survey script" : type === "usability_test" ? "Usability test script" : "Interview script";
}

function normalizeResearchScripts(scripts, { projectId = null, initiativeId = null } = {}) {
  const t = now();
  return (Array.isArray(scripts) ? scripts : [])
    .map((it, idx) => {
      const raw = safeObj(it);
      const type = normalizeResearchScriptType(raw.type);
      const sourceResearchQuestionText = safeText(raw.sourceResearchQuestionText).trim() || null;
      const title = safeText(raw.title).trim() || defaultResearchScriptTitle(type, sourceResearchQuestionText);
      const sections = (Array.isArray(raw.sections) ? raw.sections : [])
        .map((section, sIdx) => {
          const s = safeObj(section);
          const sectionTitle = safeText(s.title).trim();
          if (!sectionTitle) return null;
          const items = normalizeStringArray(s.items);
          return {
            id: safeText(s.id).trim() || `sec_${sIdx + 1}`,
            title: sectionTitle,
            items,
          };
        })
        .filter(Boolean);
      const createdAt = Number.isFinite(Number(raw.createdAt)) ? Number(raw.createdAt) : t;
      const updatedAt = Number.isFinite(Number(raw.updatedAt)) ? Number(raw.updatedAt) : createdAt;
      return {
        id: safeText(raw.id).trim() || `rs_${idx + 1}`,
        title,
        type,
        status: normalizeResearchScriptStatus(raw.status),
        sourceResearchQuestionId: safeText(raw.sourceResearchQuestionId).trim() || null,
        sourceResearchQuestionText,
        recommendedMethod: normalizeResearchScriptType(raw.recommendedMethod || type),
        projectId: safeText(raw.projectId).trim() || (projectId ? safeText(projectId).trim() : null),
        initiativeId: safeText(raw.initiativeId).trim() || (initiativeId ? safeText(initiativeId).trim() : null),
        sections,
        linkedResearchDataIds: normalizeStringArray(raw.linkedResearchDataIds),
        linkedEvidenceIds: normalizeStringArray(raw.linkedEvidenceIds),
        linkedInsightIds: normalizeStringArray(raw.linkedInsightIds),
        createdAt,
        updatedAt,
      };
    })
    .filter(Boolean);
}

function migrateInterviewQuestionsToResearchScripts(interviewQuestions, personas, { projectId = null, initiativeId = null } = {}) {
  const iq = safeObj(interviewQuestions);
  const personaById = new Map((Array.isArray(personas) ? personas : []).map((p) => [String(p?.id || ""), p]));
  const t = now();
  const scripts = [];
  Object.keys(iq).forEach((personaId, idx) => {
    const slot = iq[personaId];
    const entry = Array.isArray(slot)
      ? { items: slot }
      : isPlainObject(slot)
        ? slot
        : { items: [] };
    const items = Array.isArray(entry.items) ? entry.items : [];
    if (!items.length) return;

    const sectionsByCategory = new Map();
    items.forEach((q) => {
      const category = safeText(q?.category).trim() || "Core";
      const text = safeText(q?.text).trim();
      if (!text) return;
      const key = category;
      const current = sectionsByCategory.get(key) || [];
      current.push(text);
      sectionsByCategory.set(key, current);
    });

    const sections = Array.from(sectionsByCategory.entries()).map(([title, texts], sIdx) => ({
      id: `sec_${sIdx + 1}`,
      title,
      items: texts,
    }));

    const persona = personaById.get(personaId);
    const personaName = safeText(persona?.name).trim();
    scripts.push({
      id: `rs_migrated_${idx + 1}`,
      title: personaName ? `${personaName} interview script` : `Interview script ${idx + 1}`,
      type: "interview",
      status: "draft",
      sourceResearchQuestionId: null,
      sourceResearchQuestionText: null,
      recommendedMethod: "interview",
      projectId,
      initiativeId,
      sections,
      linkedResearchDataIds: [],
      linkedEvidenceIds: [],
      linkedInsightIds: [],
      createdAt: t,
      updatedAt: t,
    });
  });
  return scripts;
}

/* ------------------------------------------------------------------ */
/* Initiatives                                                        */
/* ------------------------------------------------------------------ */

function isInsightApproved(insight) {
  const status = typeof insight?.reviewStatus === "string" ? insight.reviewStatus : "";
  if (!status) return true;
  return status === "approved" || status === "reviewed";
}

const DECISION_STATUS_SET = new Set(["draft", "committed", "revisit"]);
const DELIVERY_PRIORITY_SET = new Set(["high", "medium", "low"]);
const DECISION_EVIDENCE_TYPES = new Set(["quote", "observation", "fact"]);

function normalizeDecisionEvidenceItems(rawEvidence) {
  const arr = Array.isArray(rawEvidence) ? rawEvidence : [];
  const seen = new Set();

  return arr
    .map((item, idx) => {
      const ev = item && typeof item === "object" ? item : {};
      const text = safeText(ev.text).trim();
      if (!text) return null;

      const typeRaw = safeText(ev.type).trim().toLowerCase();
      const type = DECISION_EVIDENCE_TYPES.has(typeRaw) ? typeRaw : "observation";
      const researchItemId = safeText(ev.researchItemId).trim() || null;
      const insightId = safeText(ev.insightId).trim() || null;

      const dedupeKey = [text, type, researchItemId || "", insightId || ""].join("::");
      if (seen.has(dedupeKey)) return null;
      seen.add(dedupeKey);

      return {
        id: safeText(ev.id).trim() || `dec_ev_${idx + 1}`,
        text,
        type,
        researchItemId,
        insightId,
      };
    })
    .filter(Boolean);
}

function normalizeDecisionConfirmationSnapshot(rawSnapshot) {
  const raw = rawSnapshot && typeof rawSnapshot === "object" ? rawSnapshot : null;
  if (!raw) return null;

  const confirmedAt = safeText(raw.confirmedAt).trim() || null;
  const confirmedBy = safeText(raw.confirmedBy).trim() || null;
  const committedInsightsVersion = Number.isFinite(Number(raw.committedInsightsVersion))
    ? Number(raw.committedInsightsVersion)
    : null;
  const decisionTitleSnapshot = safeText(raw.decisionTitleSnapshot).trim() || "";
  const descriptionSnapshot = safeText(raw.descriptionSnapshot).trim() || "";
  const whySnapshot = safeText(raw.whySnapshot).trim() || descriptionSnapshot;

  const insightsSnapshot = (Array.isArray(raw.insightsSnapshot) ? raw.insightsSnapshot : [])
    .map((item) => {
      const insight = item && typeof item === "object" ? item : {};
      const id = safeText(insight.id).trim();
      const text = safeText(insight.text).trim();
      if (!id || !text) return null;
      const title = safeText(insight.title).trim() || deriveInsightTitleFromText(text);
      const tags = normalizeStringArray(insight.tags);
      const linkedEvidence = (Array.isArray(insight.linkedEvidence) ? insight.linkedEvidence : [])
        .map((ev) => {
          const normalized = ev && typeof ev === "object" ? ev : {};
          const sourceId = safeText(normalized.sourceId).trim() || null;
          const sourceType = safeText(normalized.sourceType).trim() || null;
          const sourceName = safeText(normalized.sourceName).trim() || null;
          const excerptId = safeText(normalized.excerptId).trim() || null;
          const quote = safeText(normalized.quote || normalized.snippet || normalized.text).trim() || null;
          const startOffsetRaw = Number(normalized.startOffset ?? normalized.start);
          const endOffsetRaw = Number(normalized.endOffset ?? normalized.end);
          const startOffset = Number.isFinite(startOffsetRaw) ? startOffsetRaw : null;
          const endOffset = Number.isFinite(endOffsetRaw) ? endOffsetRaw : null;
          const evidenceLabel = safeText(normalized.evidenceLabel || normalized.label).trim() || null;
          const evidenceNote = safeText(normalized.evidenceNote || normalized.note).trim() || null;

          if (!sourceId && !sourceName && !excerptId && !quote) return null;
          return {
            ...(sourceId ? { sourceId } : {}),
            ...(sourceType ? { sourceType } : {}),
            ...(sourceName ? { sourceName } : {}),
            ...(excerptId ? { excerptId } : {}),
            ...(quote ? { quote } : {}),
            ...(startOffset != null ? { startOffset } : {}),
            ...(endOffset != null ? { endOffset } : {}),
            ...(evidenceLabel ? { evidenceLabel } : {}),
            ...(evidenceNote ? { evidenceNote } : {}),
          };
        })
        .filter(Boolean);

      return {
        id,
        title,
        text,
        ...(tags.length ? { tags } : {}),
        linkedEvidence,
      };
    })
    .filter(Boolean);

  return {
    ...(confirmedAt ? { confirmedAt } : {}),
    ...(confirmedBy ? { confirmedBy } : {}),
    committedInsightsVersion,
    decisionTitleSnapshot,
    descriptionSnapshot,
    whySnapshot,
    insightsSnapshot,
  };
}

function normalizeDecisionDeliveryStories(rawStories, decisionId, fallbackRationale = "") {
  const items = Array.isArray(rawStories) ? rawStories : [];
  const seen = new Set();

  return items
    .map((story, idx) => {
      if (!story || typeof story !== "object") return null;
      const title = safeText(story.title).trim();
      const storyText = safeText(story.story).trim();
      if (!title || !storyText) return null;

      const idRaw = safeText(story.id).trim();
      const id = idRaw || `delivery_${safeText(decisionId || "decision").trim() || "decision"}_${idx + 1}`;
      if (seen.has(id)) return null;
      seen.add(id);

      const priorityRaw = safeText(story.priority).trim().toLowerCase();
      const priority = DELIVERY_PRIORITY_SET.has(priorityRaw) ? priorityRaw : "medium";

      const linkedInsightIds = normalizeStringArray(story.linkedInsightIds);
      const acceptanceCriteria = Array.isArray(story.acceptanceCriteria)
        ? story.acceptanceCriteria.map((line) => safeText(line).trim()).filter(Boolean)
        : typeof story.acceptanceCriteria === "string"
          ? story.acceptanceCriteria
              .split(/\r?\n/)
              .map((line) => safeText(line).trim())
              .filter(Boolean)
          : [];
      const rationale =
        safeText(story.rationale).trim() ||
        safeText(fallbackRationale).trim() ||
        "Grounded in the confirmed decision and linked evidence.";

      return {
        id,
        decisionId: safeText(story.decisionId).trim() || safeText(decisionId).trim() || null,
        title,
        story: storyText,
        rationale,
        priority,
        acceptanceCriteria,
        linkedInsightIds,
        origin: safeText(story.origin).trim() || "ai",
        isEdited: story.isEdited === true,
        isReviewed: story.isReviewed === true,
        isLocked: story.isLocked === true,
      };
    })
    .filter(Boolean);
}

function normalizeDecisionItem(rawDecision, idx, insightsById) {
  const raw = rawDecision && typeof rawDecision === "object" ? rawDecision : {};
  const title = typeof raw.title === "string" ? raw.title.trim() : "";
  if (!title) return null;

  const description = typeof raw.description === "string" ? raw.description.trim() : "";
  const projectId =
    typeof raw.projectId === "string" && raw.projectId.trim() ? raw.projectId.trim() : null;
  const initiativeId =
    typeof raw.initiativeId === "string" && raw.initiativeId.trim()
      ? raw.initiativeId.trim()
      : null;
  const linkedRaw = Array.isArray(raw.linkedInsightIds)
    ? raw.linkedInsightIds
    : Array.isArray(raw.insightIds)
      ? raw.insightIds
      : Array.isArray(raw.relatedInsightIds)
        ? raw.relatedInsightIds
        : [];
  const linkedInsightIds = linkedRaw
    .map((id) => (typeof id === "string" ? id.trim() : ""))
    .filter(Boolean);
  const legacyEvidence = normalizeDecisionEvidenceItems(raw.evidence);
  const evidenceResearchItemIds = legacyEvidence.map((ev) => ev.researchItemId).filter(Boolean);
  const linkedResearchItemIds = Array.from(
    new Set([
      ...normalizeStringArray(raw.linkedResearchItemIds),
      ...normalizeStringArray(raw.researchItemIds),
      ...evidenceResearchItemIds,
    ])
  );

  const filteredIds = Array.from(new Set(linkedInsightIds));

  const statusRaw = typeof raw.status === "string" ? raw.status.trim() : "draft";
  let status = DECISION_STATUS_SET.has(statusRaw) ? statusRaw : "draft";
  const hasMissingLinkedInsights = insightsById && filteredIds.some((id) => !insightsById.has(id));
  if (status !== "committed") {
    if (status === "revisit" && !hasMissingLinkedInsights) status = "draft";
    if (hasMissingLinkedInsights) status = "revisit";
  }

  const createdBy =
    typeof raw.createdBy === "string" && raw.createdBy.trim() ? raw.createdBy.trim() : "local-user";
  const updatedBy =
    typeof raw.updatedBy === "string" && raw.updatedBy.trim() ? raw.updatedBy.trim() : createdBy;
  const createdAt = typeof raw.createdAt === "string" && raw.createdAt.trim() ? raw.createdAt.trim() : isoNow();
  const updatedAt = typeof raw.updatedAt === "string" && raw.updatedAt.trim() ? raw.updatedAt.trim() : null;
  const committedAt =
    typeof raw.committedAt === "string" && raw.committedAt.trim() ? raw.committedAt.trim() : null;
  const committedBy =
    typeof raw.committedBy === "string" && raw.committedBy.trim() ? raw.committedBy.trim() : null;
  const committedInsightsVersion = Number.isFinite(Number(raw.committedInsightsVersion))
    ? Number(raw.committedInsightsVersion)
    : null;
  const committedInsightIds = normalizeStringArray(raw.committedInsightIds);
  const staleFromInsights = raw.staleFromInsights === true;
  const staleReason = safeText(raw.staleReason).trim() || null;
  const staleAt = typeof raw.staleAt === "string" && raw.staleAt.trim() ? raw.staleAt.trim() : null;
  const confirmationSnapshot = normalizeDecisionConfirmationSnapshot(raw.confirmationSnapshot);
  const deliveryStories = normalizeDecisionDeliveryStories(raw.deliveryStories, raw.id || `dec_${idx + 1}`, description);
  return {
    id: typeof raw.id === "string" && raw.id.trim() ? raw.id.trim() : `dec_${idx + 1}`,
    title,
    description,
    ...(projectId ? { projectId } : {}),
    initiativeId,
    linkedInsightIds: filteredIds,
    linkedResearchItemIds,
    evidence: legacyEvidence,
    status,
    committedAt,
    committedBy,
    committedInsightsVersion,
    committedInsightIds,
    ...(staleFromInsights ? { staleFromInsights: true } : {}),
    ...(staleReason ? { staleReason } : {}),
    ...(staleAt ? { staleAt } : {}),
    ...(confirmationSnapshot ? { confirmationSnapshot } : {}),
    deliveryStories,
    createdBy,
    updatedBy,
    createdAt,
    ...(updatedAt ? { updatedAt } : {}),
  };
}

function normalizeDecisions(decisions, insights) {
  const items = Array.isArray(decisions) ? decisions : [];
  const insightsById = new Map(
    (Array.isArray(insights) ? insights : []).map((insight) => [insight.id, insight])
  );

  return items.map((it, idx) => normalizeDecisionItem(it, idx, insightsById)).filter(Boolean);
}

function makeEmptyInitiative(id, name = "Primary initiative") {
  const t = now();
  const init = {
    id,
    name,
    description: "",
    aiBrief: "",
    goals: [],
    successMetrics: [],
    risks: [],
    linkedInsightIds: [],
    createdAt: t,
    updatedAt: t,
    insightsUsedAt: null,
    insightsApprovalStatus: "draft",
    insightsApprovedSnapshot: null,
    insightsApprovedAt: null,
    insightsApprovedBy: null,
    insightsApprovedSnapshotHash: "",
    insightsEditedAt: null,
    insightsEditedBy: null,
    insightsLastGeneratedAt: null,
    insightsVersion: 0,
    researchUpdatedAt: null,
    researchUpdatedBy: null,
    downstreamComputedAt: null,
    researchItems: [],
    researchActorIds: null,
    researchTagSpans: { version: 1, items: [], createdAt: t, updatedAt: t },
    researchInsights: { version: 1, items: [], createdAt: t, updatedAt: t },
    atomicFacts: { version: 1, items: [], createdAt: t, updatedAt: t },
    researchVersion: 0,
    tagsVersion: 0,
    insightsMeta: { derivedFromResearchVersion: 0 },
    tagsMeta: { derivedFromResearchVersion: 0 },
    dismissedForResearchVersion: null,
    dismissedDecisionStaleSignature: null,
    dismissedArtifactFreshnessByInsightsVersion: {},
    notes: [],
    interviewScripts: [],
    insightGroups: normalizeInsightGroups(null),
    insightsSets: null,
    activeInsightsSet: "auto",
    manualMapping: { isActive: false, draftGroups: [], unassignedAtomicFactIds: [], startedAt: null, lastEditedAt: null },
    tagVocabulary: { version: 1, items: [], createdAt: t, updatedAt: t },
    tagThemes: { version: 1, themes: [], overrides: { tagToThemeId: {}, themeTitles: {} }, createdAt: t, updatedAt: t },
    storyStyle: "editorial",
    decisions: [],
  };

  for (const k of INIT_ARTIFACT_KEYS) {
    if (k === "interviewQuestions") init[k] = {};
    else if (k === "interviewScripts") init[k] = [];
    else if (k === "problemStatements") init[k] = [];
    else if (k === "jtbds") init[k] = [];
    else if (k === "storyArtifacts") init[k] = [];
    else if (k === "affinityClusters") init[k] = [];
    else if (k === "direction") init[k] = { draft: null, final: null, updatedAt: t };
    else if (k === "researchTagSpans") init[k] = init.researchTagSpans;
    else if (k === "researchInsights") init[k] = init.researchInsights;
    else if (k === "atomicFacts") init[k] = init.atomicFacts;
    else if (k === "insightGroups") init[k] = init.insightGroups;
    else if (k === "tagVocabulary") init[k] = init.tagVocabulary;
    else if (k === "tagThemes") init[k] = init.tagThemes;
    else if (k in init) continue;
    else init[k] = null;
  }

  return init;
}

function ensureInitiatives(project) {
  const p = project;
  const ini = safeObj(p.initiatives);
  let byId = safeObj(ini.byId);
  let allIds = Array.isArray(ini.allIds) ? ini.allIds.slice() : [];
  let activeId = ini.activeId || null;

  if (!allIds.length) {
    const id = uid("init");
    byId = { ...byId, [id]: makeEmptyInitiative(id) };
    allIds = [id];
    activeId = id;
  } else {
    if (!activeId || !byId[activeId]) activeId = allIds[0] || null;
    allIds.forEach((id) => {
      if (!byId[id]) byId[id] = makeEmptyInitiative(id);
    });
  }

  if (activeId && byId[activeId]) {
    const cur = safeObj(byId[activeId]);
    const next = normalizeInitiativeVersions(cur);

    next.insightGroups = normalizeInsightGroups(next.insightGroups);
    next.manualMapping = normalizeManualMapping(next.manualMapping);
    if (next.insightsUsedAt === undefined) next.insightsUsedAt = null;
    if (next.insightsApprovalStatus === undefined) next.insightsApprovalStatus = "draft";
    if (next.insightsApprovedSnapshot === undefined) next.insightsApprovedSnapshot = null;
    if (next.insightsApprovedAt === undefined) next.insightsApprovedAt = null;
    if (next.insightsApprovedBy === undefined) next.insightsApprovedBy = null;
    if (next.insightsApprovedSnapshotHash === undefined) next.insightsApprovedSnapshotHash = "";
    if (next.insightsEditedAt === undefined) next.insightsEditedAt = null;
    if (next.insightsEditedBy === undefined) next.insightsEditedBy = null;
    if (next.insightsLastGeneratedAt === undefined) next.insightsLastGeneratedAt = null;
    if (next.researchUpdatedAt === undefined) next.researchUpdatedAt = null;
    if (next.researchUpdatedBy === undefined) next.researchUpdatedBy = null;
    if (next.downstreamComputedAt === undefined) next.downstreamComputedAt = null;
    if (next.researchActorIds === undefined) next.researchActorIds = null;
    next.insightsEditedAt = Number.isFinite(Number(next.insightsEditedAt)) ? Number(next.insightsEditedAt) : null;
    next.insightsLastGeneratedAt = Number.isFinite(Number(next.insightsLastGeneratedAt))
      ? Number(next.insightsLastGeneratedAt)
      : null;
    next.researchUpdatedAt = Number.isFinite(Number(next.researchUpdatedAt))
      ? Number(next.researchUpdatedAt)
      : null;
    next.downstreamComputedAt = Number.isFinite(Number(next.downstreamComputedAt))
      ? Number(next.downstreamComputedAt)
      : null;

    next.researchItems = (Array.isArray(next.researchItems) ? next.researchItems : []).map(normalizeResearchItem);
    next.researchActorIds =
      Array.isArray(next.researchActorIds) ? normalizeStringArray(next.researchActorIds) : null;
    next.researchTagSpans = normalizeResearchTagSpans(next.researchTagSpans);
    next.interviewScripts = normalizeResearchScripts(next.interviewScripts, {
      projectId: p.id,
      initiativeId: next.id,
    });
    next.researchInsights = normalizeResearchInsights(next.researchInsights, next.researchItems.length);
    next.atomicFacts = normalizeAtomicFacts(next.atomicFacts, next.researchItems.length);
    next.problemStatements = normalizeProblemStatements(next.problemStatements);
    next.jtbds = normalizeJTBDs(next.jtbds);
    next.storyArtifacts = normalizeStoryArtifacts(next.storyArtifacts);
    next.storyStyle = normalizeStoryStyleValue(next.storyStyle);
    next.notes = normalizeInitiativeNotes(next.notes, next.id);
    next.tagVocabulary = normalizeTagVocabulary(next.tagVocabulary);
    next.tagThemes = normalizeTagThemes(next.tagThemes);
    next.direction = normalizeDirection(next.direction);
    const overviewDecisions = Array.isArray(next?.overview?.decisionsV2)
      ? next.overview.decisionsV2
      : Array.isArray(next?.overview?.decisions)
        ? next.overview.decisions
        : [];
    if (overviewDecisions.length && Array.isArray(next.decisions)) {
      const byId = new Map();
      const byTitle = new Map();
      overviewDecisions.forEach((d) => {
        if (!d || typeof d !== "object") return;
        const id = safeText(d.id).trim();
        const title = safeText(d.title).trim().toLowerCase();
        const linkedInsightIds = Array.isArray(d.linkedInsightIds)
          ? d.linkedInsightIds.map((x) => safeText(x).trim()).filter(Boolean)
          : [];
        if (!linkedInsightIds.length) return;
        if (id) byId.set(id, linkedInsightIds);
        if (title) byTitle.set(title, linkedInsightIds);
      });
      if (byId.size || byTitle.size) {
        next.decisions = next.decisions.map((d) => {
          if (!d || typeof d !== "object") return d;
          const existing = Array.isArray(d.linkedInsightIds) ? d.linkedInsightIds.filter(Boolean) : [];
          if (existing.length) return d;
          const id = safeText(d.id).trim();
          const title = safeText(d.title).trim().toLowerCase();
          const linkedInsightIds = (id && byId.get(id)) || (title && byTitle.get(title)) || [];
          return linkedInsightIds.length ? { ...d, linkedInsightIds } : d;
        });
      }
    }
    const decisionInsights = Array.isArray(next.insightsApprovedSnapshot?.approvedInsights)
      ? next.insightsApprovedSnapshot.approvedInsights
      : [];
    next.decisions = normalizeDecisions(
      next.decisions,
      decisionInsights.length ? decisionInsights : next.researchInsights.items
    );
    next.description = safeText(next.description).trim();
    next.aiBrief = safeText(next.aiBrief).trim();
    next.goals = normalizeStringArray(next.goals);
    next.successMetrics = normalizeStringArray(next.successMetrics);
    next.risks = normalizeStringArray(next.risks);
    next.linkedInsightIds = normalizeStringArray(next.linkedInsightIds);
    const autoSet = normalizeInsightsSet(next?.insightsSets?.auto, next.researchItems.length, next.researchInsights, next.insightGroups);
    const manualSet = next?.insightsSets?.manual
      ? normalizeInsightsSet(next.insightsSets.manual, next.researchItems.length, null, null)
      : null;
    next.insightsSets = { auto: autoSet, ...(manualSet ? { manual: manualSet } : {}) };
    const active = next.activeInsightsSet === "manual" ? "manual" : "auto";
    next.activeInsightsSet = manualSet && active === "manual" ? "manual" : "auto";
    if (next.activeInsightsSet === "manual" && manualSet) {
      next.researchInsights = manualSet.researchInsights;
      next.insightGroups = manualSet.insightGroups;
    } else {
      next.researchInsights = autoSet.researchInsights;
      next.insightGroups = autoSet.insightGroups;
    }

    const synced = syncInsightGroupsWithThemes(next.insightGroups, next.researchInsights.items, next.tagThemes);
    next.insightGroups = synced.insightGroups;
    next.researchInsights = { ...next.researchInsights, items: synced.items };
    if (next.activeInsightsSet === "manual" && manualSet) {
      next.insightsSets = {
        ...next.insightsSets,
        manual: { ...manualSet, researchInsights: next.researchInsights, insightGroups: next.insightGroups },
      };
    } else {
      next.insightsSets = {
        ...next.insightsSets,
        auto: { ...autoSet, researchInsights: next.researchInsights, insightGroups: next.insightGroups },
      };
    }

    byId = { ...byId, [activeId]: next };
  }

  p.initiatives = { byId, allIds, activeId };
  return p;
}

/* ------------------------------------------------------------------ */
/* Public API                                                         */
/* ------------------------------------------------------------------ */

export function makeEmptyProject({
  id,
  workspaceId,
  projectName = "Untitled project",
  projectType = "new",
  productDescription = "",
  parentProjectId = null,
} = {}) {
  const t = now();
  const projId = id || uid("proj");

  const base = {
    id: projId,
    meta: {
      workspaceId,
      projectName,
      projectType: normalizeProjectType(projectType),
      productDescription,
      parentProjectId: parentProjectId ?? null,
      createdAt: t,
      updatedAt: t,
    },
    personas: [],
    primaryPersonaId: null,
    activity: [],
    team: { ...DEFAULT_TEAM },
    initiatives: null,
  };

  return normalizeProject(base);
}

export function normalizeProject(project) {
  if (!project || typeof project !== "object") return null;

  const p = { ...project };
  p.meta = { ...safeObj(p.meta) };

  if (!p.id) p.id = uid("proj");

  if (!p.meta.createdAt) p.meta.createdAt = now();
  if (!p.meta.updatedAt) p.meta.updatedAt = p.meta.createdAt;

  p.meta.projectType = normalizeProjectType(p.meta.projectType);
  if (!p.meta.projectName) p.meta.projectName = "Untitled project";
  if (p.meta.productDescription == null) p.meta.productDescription = "";
  if (!p.meta.workspaceId && project.workspaceId) p.meta.workspaceId = project.workspaceId;
  if (p.meta.parentProjectId === undefined || p.meta.parentProjectId === "") p.meta.parentProjectId = null;

  p.team = normalizeTeam(p.team);
  p.activity = normalizeActivity(p.activity);

  const personas = Array.isArray(p.personas) ? p.personas : [];
  p.personas = personas.map((persona, idx) => {
    const pp = { ...safeObj(persona) };

    if (!pp.id) pp.id = `p${idx + 1}`;
    if (!pp.name) pp.name = "Unnamed persona";
    if (!pp.role) pp.role = "Persona";
    if (pp.age == null) pp.age = "";

    const ver = Number(pp.version);
    pp.version = Number.isFinite(ver) ? ver : 0;

    if (!pp.createdAt) pp.createdAt = now();
    if (!pp.updatedAt) pp.updatedAt = pp.createdAt;
    if (!pp.lastEditedAt) pp.lastEditedAt = pp.createdAt;

    const originRaw = typeof pp.origin === "string" ? pp.origin.trim().toLowerCase() : "";
    pp.origin = originRaw === "user" || originRaw === "manual" ? "user" : "ai";

    pp.isEdited = toBool(pp.isEdited ?? false);
    pp.isReviewed = toBool(pp.isReviewed ?? false);
    pp.isLocked = toBool(pp.isLocked ?? false);

    if (pp.avatarImage == null) pp.avatarImage = null;
    if (pp.reviewedAt == null) pp.reviewedAt = null;

    return pp;
  });

  if (p.primaryPersonaId == null) p.primaryPersonaId = null;

  ensureInitiatives(p);

  const activeId = p?.initiatives?.activeId;
  if (activeId && p?.initiatives?.byId?.[activeId]) {
    const cur = safeObj(p.initiatives.byId[activeId]);
    const next = normalizeInitiativeVersions(cur);

    next.insightGroups = normalizeInsightGroups(next.insightGroups);
    next.researchItems = (Array.isArray(next.researchItems) ? next.researchItems : []).map(normalizeResearchItem);
    next.researchTagSpans = normalizeResearchTagSpans(next.researchTagSpans);
    next.researchInsights = normalizeResearchInsights(next.researchInsights, next.researchItems.length);
    next.tagVocabulary = normalizeTagVocabulary(next.tagVocabulary);
    next.tagThemes = normalizeTagThemes(next.tagThemes);
    next.problemStatements = normalizeProblemStatements(next.problemStatements);
    next.interviewQuestions = normalizeInterviewQuestionsMap(next.interviewQuestions, p.personas);
    next.interviewScripts = normalizeResearchScripts(next.interviewScripts, {
      projectId: p.id,
      initiativeId: next.id,
    });
    const shouldMigrateLegacyInterviewScripts =
      cur.interviewScripts == null && !next.interviewScripts.length;
    if (shouldMigrateLegacyInterviewScripts) {
      next.interviewScripts = migrateInterviewQuestionsToResearchScripts(next.interviewQuestions, p.personas, {
        projectId: p.id,
        initiativeId: next.id,
      });
    }
    next.direction = normalizeDirection(next.direction);
    next.decisions = normalizeDecisions(next.decisions, next.researchInsights.items);
    next.description = safeText(next.description).trim();
    next.aiBrief = safeText(next.aiBrief).trim();
    next.goals = normalizeStringArray(next.goals);
    next.successMetrics = normalizeStringArray(next.successMetrics);
    next.risks = normalizeStringArray(next.risks);
    next.linkedInsightIds = normalizeStringArray(next.linkedInsightIds);

    p.initiatives = {
      ...p.initiatives,
      byId: { ...p.initiatives.byId, [activeId]: next },
    };
  }

  return p;
}

function normalizeTeam(rawTeam) {
  const team = safeObj(rawTeam);
  const owner = typeof team.owner === "string" && team.owner.trim() ? team.owner.trim() : DEFAULT_TEAM.owner;
  const editors = Array.isArray(team.editors)
    ? team.editors.map((id) => (typeof id === "string" ? id.trim() : "")).filter(Boolean)
    : DEFAULT_TEAM.editors.slice();
  const viewers = Array.isArray(team.viewers)
    ? team.viewers.map((id) => (typeof id === "string" ? id.trim() : "")).filter(Boolean)
    : DEFAULT_TEAM.viewers.slice();

  return { owner, editors, viewers };
}

function normalizeActivityEntry(rawEntry, idx) {
  const entry = rawEntry && typeof rawEntry === "object" ? rawEntry : {};
  const entityType = typeof entry.entityType === "string" ? entry.entityType : "";
  const action = typeof entry.action === "string" ? entry.action : "";
  const actor = typeof entry.actor === "string" ? entry.actor : "";
  const entityId = typeof entry.entityId === "string" ? entry.entityId.trim() : "";
  const shortDescription = typeof entry.shortDescription === "string" ? entry.shortDescription.trim() : "";
  if (!ACTIVITY_ENTITY_TYPES.has(entityType) || !ACTIVITY_ACTIONS.has(action) || !ACTIVITY_ACTORS.has(actor) || !entityId)
    return null;

  const initiativeId =
    typeof entry.initiativeId === "string" && entry.initiativeId.trim() ? entry.initiativeId.trim() : null;
  const timestamp = typeof entry.timestamp === "string" && entry.timestamp.trim() ? entry.timestamp.trim() : isoNow();
  const id = typeof entry.id === "string" && entry.id.trim() ? entry.id.trim() : `act_${idx + 1}`;

  return {
    id,
    entityType,
    entityId,
    initiativeId,
    action,
    actor,
    timestamp,
    shortDescription,
  };
}

function normalizeActivity(activity) {
  const items = Array.isArray(activity) ? activity : [];
  return items.map((it, idx) => normalizeActivityEntry(it, idx)).filter(Boolean);
}

function normalizeCurrentUser(rawUser) {
  const user = safeObj(rawUser);
  const id = typeof user.id === "string" && user.id.trim() ? user.id.trim() : "local-user";
  const role = user.role === "member" ? "member" : "admin";
  return { id, role };
}

function normalizeFeedbackContext(rawContext) {
  const ctx = safeObj(rawContext);
  const route = typeof ctx.route === "string" && ctx.route.trim() ? ctx.route.trim() : "/";
  const activeProjectId =
    typeof ctx.activeProjectId === "string" && ctx.activeProjectId.trim() ? ctx.activeProjectId.trim() : null;
  const activeInitiativeId =
    typeof ctx.activeInitiativeId === "string" && ctx.activeInitiativeId.trim() ? ctx.activeInitiativeId.trim() : null;
  const entityType =
    typeof ctx.entityType === "string" && ctx.entityType.trim() ? ctx.entityType.trim() : null;
  const entityId = typeof ctx.entityId === "string" && ctx.entityId.trim() ? ctx.entityId.trim() : null;

  return {
    route,
    activeProjectId,
    activeInitiativeId,
    entityType,
    entityId,
    ...(ctx.schemaVersion != null ? { schemaVersion: ctx.schemaVersion } : {}),
    ...(typeof ctx.appVersion === "string" && ctx.appVersion.trim() ? { appVersion: ctx.appVersion.trim() } : {}),
    ...(typeof ctx.isAI === "boolean" ? { isAI: ctx.isAI } : {}),
  };
}

function normalizeFeedbackItem(rawEntry, idx) {
  const entry = rawEntry && typeof rawEntry === "object" ? rawEntry : {};
  const typeRaw = typeof entry.type === "string" ? entry.type : "other";
  const type = FEEDBACK_TYPES.has(typeRaw) ? typeRaw : "other";
  const statusRaw = typeof entry.status === "string" ? entry.status : "new";
  const status = FEEDBACK_STATUSES.has(statusRaw) ? statusRaw : "new";
  const severityRaw = typeof entry.severity === "string" ? entry.severity : null;
  const severity = FEEDBACK_SEVERITIES.has(severityRaw) ? severityRaw : null;
  const message = typeof entry.message === "string" ? entry.message.trim() : "";
  if (!message) return null;

  const createdAt = typeof entry.createdAt === "string" && entry.createdAt.trim() ? entry.createdAt.trim() : isoNow();
  const createdByUserId =
    typeof entry.createdByUserId === "string" && entry.createdByUserId.trim()
      ? entry.createdByUserId.trim()
      : "local-user";

  const context = normalizeFeedbackContext(entry.context);
  const id = typeof entry.id === "string" && entry.id.trim() ? entry.id.trim() : `fb_${idx + 1}`;

  return {
    id,
    type,
    severity,
    status,
    message,
    createdAt,
    createdByUserId,
    context,
    ...(entry.meta && typeof entry.meta === "object" ? { meta: entry.meta } : {}),
  };
}

function normalizeFeedbackList(list) {
  const items = Array.isArray(list) ? list : [];
  return items.map((it, idx) => normalizeFeedbackItem(it, idx)).filter(Boolean);
}

/* ------------------------------------------------------------------ */
/* Workspace normalization                                             */
/* ------------------------------------------------------------------ */

export function normalizeWorkspaceState(state, _version = 1) {
  const s = state && typeof state === "object" ? { ...state } : {};

  const workspaces = s.workspaces && typeof s.workspaces === "object" ? s.workspaces : { byId: {}, allIds: [] };
  const projects = s.projects && typeof s.projects === "object" ? s.projects : { byId: {}, allIds: [] };

  const wsById = safeObj(workspaces.byId);
  const wsAllIds = Array.isArray(workspaces.allIds) ? workspaces.allIds.slice() : [];

  const projById = safeObj(projects.byId);
  const projAllIds = Array.isArray(projects.allIds) ? projects.allIds.slice() : [];

  // Recover ids from byId when allIds is missing to avoid orphaned data on rehydrate.
  if (!wsAllIds.length) {
    const wsIds = Object.keys(wsById);
    if (wsIds.length) wsAllIds.push(...wsIds);
  }

  if (!projAllIds.length) {
    const projIds = Object.keys(projById);
    if (projIds.length) projAllIds.push(...projIds);
  }

  const fixedProjById = { ...projById };
  projAllIds.forEach((pid) => {
    fixedProjById[pid] = normalizeProject(fixedProjById[pid]);
  });

  let activeWorkspaceId = s.activeWorkspaceId;
  if (!wsAllIds.length) {
    const wsId = uid("ws");
    wsById[wsId] = { id: wsId, name: "My workspace", createdAt: now(), updatedAt: now() };
    wsAllIds.push(wsId);
    activeWorkspaceId = wsId;
  } else if (!activeWorkspaceId || !wsById[activeWorkspaceId]) {
    activeWorkspaceId = wsAllIds[0];
  }

  if (!projAllIds.length) {
    const projId = uid("proj");
    const empty = makeEmptyProject({
      id: projId,
      workspaceId: activeWorkspaceId,
      projectName: "Untitled project",
      projectType: "new",
      parentProjectId: null,
    });
    fixedProjById[projId] = empty;
    projAllIds.push(projId);
  }

  const ap = safeObj(s.activeProjectByWorkspaceId);
  const nextAp = { ...ap };

  wsAllIds.forEach((wsId) => {
    const currentActive = nextAp[wsId];
    const candidates = projAllIds
      .map((pid) => fixedProjById[pid])
      .filter(Boolean)
      .filter((p) => p?.meta?.workspaceId === wsId);

    if (!currentActive || !fixedProjById[currentActive] || fixedProjById[currentActive]?.meta?.workspaceId !== wsId) {
      nextAp[wsId] = candidates[0]?.id || null;
    }

    if (!nextAp[wsId]) {
      const projId = uid("proj");
      const created = makeEmptyProject({
        id: projId,
        workspaceId: wsId,
        projectName: "Untitled project",
        projectType: "new",
        parentProjectId: null,
      });
      fixedProjById[projId] = created;
      projAllIds.push(projId);
      nextAp[wsId] = projId;
    }
  });

  // Drop any workspace mappings that no longer exist.
  Object.keys(nextAp).forEach((wsId) => {
    if (!wsById[wsId]) delete nextAp[wsId];
  });

  const ai = safeObj(s.activeInitiativeByProjectId);
  const nextAi = { ...ai };
  projAllIds.forEach((pid) => {
    const proj = fixedProjById[pid];
    const candidate = nextAi[pid];
    const fallback = proj?.initiatives?.activeId || proj?.initiatives?.allIds?.[0] || null;
    // Guard against stale active initiative ids after reload by validating against initiatives.byId.
    const initId =
      (candidate && proj?.initiatives?.byId?.[candidate] ? candidate : null) ||
      (fallback && proj?.initiatives?.byId?.[fallback] ? fallback : null) ||
      null;
    if (initId) nextAi[pid] = initId;
  });

  // Drop any initiative mappings that no longer exist.
  Object.keys(nextAi).forEach((pid) => {
    if (!fixedProjById[pid]) delete nextAi[pid];
  });

  return {
    schemaVersion: s.schemaVersion ?? 3,
    workspaces: { byId: wsById, allIds: wsAllIds },
    projects: { byId: fixedProjById, allIds: projAllIds },
    activeWorkspaceId,
    activeProjectByWorkspaceId: nextAp,
    activeInitiativeByProjectId: nextAi,
    researchFocus: s.researchFocus ?? null,
    targetInsightId: s.targetInsightId ?? null,
    currentUser: normalizeCurrentUser(s.currentUser),
    feedback: normalizeFeedbackList(s.feedback),
  };
}
